import cx from 'classnames';
import {
  ElementType,
  FormEvent,
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  DetailedHTMLProps,
  FormHTMLAttributes
} from 'react';
import { FormContextValue, FormLabelSize, FormSize, IUseForm, IUseFormProps, TFormValues } from './types';
import { useForm } from './useForm';

const FormContext = createContext<FormContextValue<any>>(null as any);

export function useFormContext<T extends TFormValues>() {
  if (!FormContext) {
    throw new Error('useFormContext must be used within a Form');
  }

  return useContext<FormContextValue<T>>(FormContext);
}

type FormElementProps = DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>;

export interface ICreateFormProps<Values extends TFormValues>
  extends IUseFormProps<Values>,
    Omit<FormElementProps, 'onSubmit' | 'children'> {
  ignoreHittingEnter?: boolean;
  values?: Values;
  children: ((form: IUseForm<Values>) => ReactNode) | ReactNode;
  element?: ElementType;
  form?: undefined;
  size?: FormSize;
  labelSize?: FormLabelSize;
  theme?: 'light' | 'dark';
}

export interface IFormProviderProps<Values extends TFormValues>
  extends Omit<FormElementProps, 'children' | 'onSubmit'> {
  form: IUseForm<Values>;
  element?: ElementType;
  renderOnlyIfInitialized?: boolean;
  size?: FormSize;
  labelSize?: FormLabelSize;
  theme?: 'light' | 'dark';
  showModifiedIndicator?: boolean;
  children?: ReactNode;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
}

export type TFormProps<Values extends TFormValues> = IFormProviderProps<Values> | ICreateFormProps<Values>;

const INITIAL_VALUES = {};

// Overload 1: When form prop is provided (external form)
export function Form<Values extends TFormValues>(props: IFormProviderProps<Values>): ReactElement;
// Overload 2: When creating a new form internally
export function Form<Values extends TFormValues = TFormValues>(props: ICreateFormProps<Values>): ReactElement;
// Implementation
export function Form<Values extends TFormValues = TFormValues>(props: TFormProps<Values>) {
  if ('form' in props) {
    if (!props.form) {
      throw new Error('form is required');
    }

    const { form, onSubmit, className, element, renderOnlyIfInitialized, size, labelSize, theme, showModifiedIndicator, ...formProps } = props;

    const FormComponent = element || 'form';
    const shouldRenderChildren = renderOnlyIfInitialized ? form.isInitialized : true;

    const contextValue: FormContextValue<Values> = useMemo(
      () => ({
        ...form,
        size,
        labelSize,
        theme,
        showModifiedIndicator
      }),
      [form, size, labelSize, theme, showModifiedIndicator]
    );

    return (
      <FormComponent
        {...formProps}
        className={cx('rio-form-v2', className, size && `rio-form-v2--${size}`)}
        onSubmit={onSubmit}
      >
        <FormContext.Provider value={contextValue}>{shouldRenderChildren ? props.children : null}</FormContext.Provider>
      </FormComponent>
    );
  } else {
    return <CreateForm {...(props as ICreateFormProps<Values>)} />;
  }
}

export function CreateForm<Values extends TFormValues>(props: ICreateFormProps<Values>) {
  const {
    initialValues = INITIAL_VALUES as Values,
    ignoreHittingEnter = false,
    onSubmit,
    className,
    values,
    element,
    readOnly,
    validationSchema,
    resetTriggers,
    size,
    labelSize,
    ...restProps
  } = props;

  const form = useForm<Values>({
    initialValues,
    readOnly,
    validationSchema,
    resetTriggers,
    onSubmit
  });

  useEffect(() => {
    if (values) {
      form.onChangeValues(values);
    }
  }, [values]);

  const handleKeyDown = (event: FormEvent<HTMLFormElement>) => {
    if (
      (event.target as any).nodeName === 'INPUT' &&
      (event.nativeEvent as any).key === 'Enter' &&
      ignoreHittingEnter
    ) {
      event.preventDefault();
    }
  };

  const childrenIsFunction = typeof props.children === 'function';

  const FormComponent = element || 'form';

  return (
    <FormContext.Provider value={{ ...form, size, labelSize }}>
      <FormComponent
        {...restProps}
        className={cx('rio-form-v2', className, size && `rio-form-v2--${size}`)}
        onKeyDown={handleKeyDown}
        onSubmit={onSubmit ? form.formSubmitHandler(() => {}) : undefined}
      >
        {childrenIsFunction ? (props.children as any)(form) : props.children}
      </FormComponent>
    </FormContext.Provider>
  );
}

export function FormProvider<Values extends Record<string, any>>({
  form,
  size,
  children
}: {
  form: IUseForm<Values>;
  size?: FormSize;
  children: ReactNode;
}) {
  return <FormContext.Provider value={{ ...form, size }}>{children}</FormContext.Provider>;
}

export const WithFormContext = <Values extends TFormValues>({
  children
}: {
  children: (form: IUseForm<Values>) => ReactElement;
}) => {
  const form = useFormContext<Values>();
  return children(form);
};
